<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="선착순 줍기"
        subtitle="24시간 내 연락되지 않은 유실 DB"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 좌측: 요약 정보 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 줍기 가능 DB 카드 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-5">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-[14px] font-bold text-slate-900">줍기 가능 DB</p>
                  <p class="text-[12px] text-slate-400">선착순 배정</p>
                </div>
              </div>
              <div class="flex items-end justify-between">
                <div>
                  <p class="text-[11px] text-slate-400 mb-1">현재 대기</p>
                  <p class="text-[36px] font-black text-blue-600 leading-none">{{ expiredLeads.length }}</p>
                </div>
                <p class="text-[13px] text-slate-400 pb-1">건</p>
              </div>
            </div>

            <!-- 내 현장 정보 -->
            <div v-if="currentProperty" class="bg-white border border-slate-200 rounded-2xl p-4">
              <p class="text-[12px] font-bold text-slate-500 mb-3">내 현장</p>
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-[14px] font-bold text-slate-900 truncate">{{ currentProperty.propertyName }}</p>
                  <p class="text-[12px] text-slate-400">같은 현장의 유실 DB만 표시됩니다</p>
                </div>
              </div>
            </div>

            <!-- 현장 미소속 -->
            <div v-else class="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div class="flex gap-3">
                <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-[13px] font-bold text-amber-800 mb-1">현장 소속 필요</p>
                  <p class="text-[12px] text-amber-700 mb-2">현장에 소속되어야 선착순 줍기를 이용할 수 있습니다.</p>
                  <router-link to="/partners/property/change" class="text-[12px] font-bold text-amber-600 hover:underline">
                    현장 소속 신청하기 →
                  </router-link>
                </div>
              </div>
            </div>

            <!-- 파트너스 포인트 -->
            <div class="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                  <span class="text-[14px] font-black text-white">PP</span>
                </div>
                <div>
                  <p class="text-[14px] font-bold text-slate-900">파트너스 포인트</p>
                  <p class="text-[12px] text-slate-400">콜 가져가기 시 {{ formatNumber(PURCHASE_PRICE) }}P 차감</p>
                </div>
              </div>
              <div class="flex items-end justify-between">
                <div>
                  <p class="text-[11px] text-slate-400 mb-1">현재 잔액</p>
                  <p class="text-[28px] font-black text-blue-600 leading-none">{{ formatNumber(pointBalance) }}</p>
                </div>
                <p class="text-[13px] font-bold text-blue-600 pb-1">P</p>
              </div>
              <router-link
                to="/partners/point"
                class="mt-4 w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white text-[13px] font-bold rounded-xl hover:bg-blue-700 transition"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
                포인트 충전
              </router-link>
            </div>

            <!-- 안내사항 -->
            <div class="bg-blue-50 border border-blue-200 rounded-2xl p-4">
              <div class="flex gap-3">
                <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-[13px] font-bold text-blue-800 mb-1">선착순 줍기 안내</p>
                  <ul class="text-[12px] text-blue-700 space-y-1">
                    <li>• 원래 담당자가 24시간 내 연락하지 않은 DB입니다</li>
                    <li>• "가져가기" 클릭 시 {{ formatNumber(PURCHASE_PRICE) }}P가 차감됩니다</li>
                    <li>• 먼저 가져가는 상담사에게 배정됩니다</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- 바로가기 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-4">
              <p class="text-[12px] font-bold text-slate-500 mb-3">바로가기</p>
              <div class="space-y-2">
                <router-link to="/partners/leads/designated" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">신규 지목 DB</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
                <router-link to="/partners/leads/customers" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">내 고객 보관함</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- 우측: DB 리스트 -->
          <div class="lg:col-span-2">
            <!-- 로딩 -->
            <div v-if="loading" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center">
              <div class="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p class="text-[14px] text-slate-400">데이터를 불러오는 중...</p>
            </div>

            <!-- 현장 미소속 -->
            <div v-else-if="!currentProperty" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center">
              <div class="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <p class="text-[15px] font-medium text-slate-500 mb-1">소속된 현장이 없습니다</p>
              <p class="text-[13px] text-slate-400 mb-4">현장에 소속되어야 선착순 줍기를 이용할 수 있습니다</p>
              <router-link to="/partners/property/change" class="px-4 py-2 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition">
                현장 소속 신청하기
              </router-link>
            </div>

            <!-- 빈 상태 -->
            <div v-else-if="expiredLeads.length === 0" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center">
              <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <p class="text-[15px] font-medium text-slate-500 mb-1">줍기 가능한 DB가 없습니다</p>
              <p class="text-[13px] text-slate-400">{{ currentProperty?.propertyName }} 현장에 유실된 DB가 없습니다</p>
            </div>

            <!-- 리스트 -->
            <div v-else class="space-y-3">
              <div class="flex items-center justify-between mb-2">
                <p class="text-[13px] font-bold text-slate-700">유실 DB 목록</p>
                <p class="text-[12px] text-slate-400">총 {{ expiredLeads.length }}건</p>
              </div>

              <div
                v-for="lead in expiredLeads"
                :key="lead.id"
                class="bg-white border border-slate-200 rounded-2xl overflow-hidden"
              >
                <!-- 유실 배지 -->
                <div class="flex items-center justify-between gap-2 px-4 py-2 bg-amber-50 text-amber-600">
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span class="text-[13px] font-bold">유실됨 · {{ getExpiredTime(lead) }}</span>
                  </div>
                  <span class="text-[11px] text-amber-500">원래 담당: {{ lead.partnerDisplayName }}</span>
                </div>

                <!-- 고객 정보 -->
                <div class="p-4">
                  <div class="flex items-center justify-between mb-3">
                    <h3 class="text-[16px] font-bold text-slate-900">{{ maskName(lead.customerName) }}</h3>
                    <span class="px-2.5 py-1 bg-blue-100 text-blue-700 text-[11px] font-bold rounded-lg">
                      {{ lead.propertyName }}
                    </span>
                  </div>

                  <div class="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p class="text-[11px] text-slate-400 mb-0.5">연락처</p>
                      <p class="text-[13px] font-medium text-slate-500">{{ maskPhone(lead.customerPhone) }}</p>
                    </div>
                    <div>
                      <p class="text-[11px] text-slate-400 mb-0.5">신청일시</p>
                      <p class="text-[13px] font-medium text-slate-700">{{ formatDate(lead.createdAt) }}</p>
                    </div>
                  </div>

                  <p class="text-[11px] text-slate-400 italic">
                    "가져가기" 클릭 시 {{ formatNumber(PURCHASE_PRICE) }}P가 차감되고 내 고객으로 배정됩니다.
                  </p>
                </div>

                <!-- 액션 버튼 -->
                <div class="px-4 py-3 bg-slate-50 border-t border-slate-100">
                  <button
                    @click="pickLead(lead)"
                    :disabled="actionLoading === lead.id || pointBalance < PURCHASE_PRICE"
                    class="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition disabled:bg-slate-300 disabled:cursor-not-allowed"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    <template v-if="actionLoading === lead.id">처리 중...</template>
                    <template v-else-if="pointBalance < PURCHASE_PRICE">포인트 부족</template>
                    <template v-else>가져가기 ({{ formatNumber(PURCHASE_PRICE) }}P)</template>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 성공 모달 -->
    <div v-if="showSuccessModal" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" @click.self="closeSuccessModal">
      <div class="w-full max-w-md bg-white rounded-2xl overflow-hidden">
        <div class="flex flex-col items-center px-5 py-6 bg-blue-50">
          <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h3 class="text-[18px] font-bold text-slate-900">가져오기 완료!</h3>
        </div>

        <div class="p-5">
          <div class="bg-slate-50 rounded-xl p-4 space-y-3 mb-4">
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-slate-500">고객명</span>
              <span class="text-[14px] font-bold text-slate-900">{{ pickedLead?.customerName }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-slate-500">연락처</span>
              <a :href="`tel:${pickedLead?.customerPhone}`" class="text-[16px] font-black text-blue-600 hover:underline">
                {{ pickedLead?.customerPhone }}
              </a>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-slate-500">현장</span>
              <span class="text-[14px] font-medium text-slate-700">{{ pickedLead?.propertyName }}</span>
            </div>
          </div>

          <p class="text-[12px] text-slate-400 text-center">
            지금 바로 고객에게 연락해보세요!
          </p>
        </div>

        <div class="flex gap-3 px-5 py-4 bg-slate-50">
          <button
            @click="closeSuccessModal"
            class="flex-1 py-3 border border-slate-300 text-slate-600 text-[14px] font-bold rounded-xl hover:bg-slate-100 transition"
          >
            닫기
          </button>
          <router-link
            to="/partners/leads/customers"
            class="flex-1 py-3 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition text-center"
          >
            내 고객 보관함
          </router-link>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query, where, getDocs, doc, getDoc, runTransaction } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

const PURCHASE_PRICE = 20000

export default {
  name: 'OpenLeadsView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const { user, userProfile } = useAuth()
    const loading = ref(true)
    const leads = ref([])
    const currentProperty = ref(null)
    const actionLoading = ref(null)
    const showSuccessModal = ref(false)
    const pickedLead = ref(null)
    const pointBalance = ref(0)

    const expiredLeads = computed(() => {
      return leads.value.filter(lead => lead.status === 'expired')
    })

    const loadPointBalance = async () => {
      if (!user.value) return

      try {
        const pointDoc = await getDoc(doc(db, 'partnerPoints', user.value.uid))
        if (pointDoc.exists()) {
          pointBalance.value = pointDoc.data().balance || 0
        } else {
          pointBalance.value = 0
        }
      } catch (error) {
        console.error('포인트 잔액 로드 실패:', error)
      }
    }

    const loadCurrentProperty = async () => {
      if (!user.value) return

      try {
        const q = query(
          collection(db, 'propertyPartners'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'active')
        )
        const snapshot = await getDocs(q)
        if (!snapshot.empty) {
          currentProperty.value = {
            id: snapshot.docs[0].id,
            ...snapshot.docs[0].data()
          }
        }
      } catch (error) {
        console.error('소속 현장 로드 실패:', error)
      }
    }

    const loadExpiredLeads = async () => {
      if (!currentProperty.value) {
        loading.value = false
        return
      }

      try {
        loading.value = true
        const q = query(
          collection(db, 'consultationRequests'),
          where('propertyId', '==', currentProperty.value.propertyId),
          where('status', '==', 'expired')
        )
        const snapshot = await getDocs(q)
        leads.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('유실 DB 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const getExpiredTime = (lead) => {
      const expiresAt = new Date(lead.expiresAt)
      const now = new Date()
      const diff = now - expiresAt
      const hours = Math.floor(diff / (1000 * 60 * 60))

      if (hours < 1) return '방금 전'
      if (hours < 24) return `${hours}시간 전`
      const days = Math.floor(hours / 24)
      return `${days}일 전`
    }

    const maskName = (name) => {
      if (!name || name.length < 2) return '***'
      return name.charAt(0) + '*'.repeat(name.length - 1)
    }

    const maskPhone = (phone) => {
      if (!phone) return '***-****-****'
      const parts = phone.split('-')
      if (parts.length === 3) {
        return `${parts[0]}-****-${parts[2].slice(-2)}**`
      }
      return '***-****-****'
    }

    const formatDate = (dateStr) => {
      const date = new Date(dateStr)
      return date.toLocaleString('ko-KR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatNumber = (num) => {
      return num?.toLocaleString() || '0'
    }

    const pickLead = async (lead) => {
      if (!user.value || !userProfile.value) return

      if (pointBalance.value < PURCHASE_PRICE) {
        alert(`포인트가 부족합니다.\n필요: ${formatNumber(PURCHASE_PRICE)}P\n보유: ${formatNumber(pointBalance.value)}P`)
        return
      }

      if (!confirm(`${formatNumber(PURCHASE_PRICE)}P를 사용하여 콜을 가져가시겠습니까?`)) {
        return
      }

      actionLoading.value = lead.id

      try {
        const now = new Date()
        const pointRef = doc(db, 'partnerPoints', user.value.uid)
        const leadRef = doc(db, 'consultationRequests', lead.id)
        const transactionRef = doc(collection(db, 'pointTransactions'))

        let displayName = userProfile.value.name || '상담사'
        if (currentProperty.value) {
          displayName = currentProperty.value.displayName || displayName
        }

        await runTransaction(db, async (transaction) => {
          const pointDoc = await transaction.get(pointRef)

          if (!pointDoc.exists()) {
            throw new Error('포인트 정보가 없습니다.')
          }

          const currentBalance = pointDoc.data().balance || 0
          if (currentBalance < PURCHASE_PRICE) {
            throw new Error('포인트가 부족합니다.')
          }

          const newBalance = currentBalance - PURCHASE_PRICE

          // 포인트 차감
          transaction.update(pointRef, {
            balance: newBalance,
            totalUsed: (pointDoc.data().totalUsed || 0) + PURCHASE_PRICE,
            updatedAt: now.toISOString()
          })

          // 거래 내역 기록
          transaction.set(transactionRef, {
            partnerId: user.value.uid,
            type: 'use',
            amount: -PURCHASE_PRICE,
            balanceAfter: newBalance,
            description: `선착순 줍기 DB 구매 - ${lead.customerName}`,
            relatedId: lead.id,
            createdAt: now.toISOString()
          })

          // 상담 신청 상태 변경
          transaction.update(leadRef, {
            status: 'picked',
            pickedBy: user.value.uid,
            pickedByDisplayName: displayName,
            pickedAt: now.toISOString(),
            updatedAt: now.toISOString()
          })
        })

        // 성공 시 UI 업데이트
        pointBalance.value -= PURCHASE_PRICE
        pickedLead.value = lead
        showSuccessModal.value = true
        leads.value = leads.value.filter(l => l.id !== lead.id)

      } catch (error) {
        console.error('가져오기 실패:', error)
        alert(error.message || '처리에 실패했습니다. 다시 시도해주세요.')
      } finally {
        actionLoading.value = null
      }
    }

    const closeSuccessModal = () => {
      showSuccessModal.value = false
      pickedLead.value = null
    }

    onMounted(async () => {
      await loadPointBalance()
      await loadCurrentProperty()
      await loadExpiredLeads()
    })

    return {
      loading,
      currentProperty,
      expiredLeads,
      actionLoading,
      showSuccessModal,
      pickedLead,
      pointBalance,
      PURCHASE_PRICE,
      getExpiredTime,
      maskName,
      maskPhone,
      formatDate,
      formatNumber,
      pickLead,
      closeSuccessModal
    }
  }
}
</script>
